import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { pageContext, sanitizeInnerHtml } from 'ctm-util-functions';
import { getOfferDetails } from 'ctm-app/client/sagas/state_selectors';
import { offerShow } from 'ctm-app/client/sagas/utils/process';
import { availableProductResultsSelector } from 'ctm-selectors';
import styles from './results_propensity_offer_banner.css';

/**
 * _ResultPageOfferBanner component renders an offer banner based on the offerDetails prop.
 * @param {Object} props - The component props.
 * @param {Object} props.offerDetails - The details of the offer to be displayed in the banner.
 * @param {Array} props.offerDetails.offers - An array of offer objects.
 * @param {Object} props.offerDetails.offers[0].offer - The offer object.
 * @param {string} props.offerDetails.offers[0].offer.title - The title of the offer.
 * @param {string} props.offerDetails.offers[0].offer.imageUrl - The URL of the offer's image.
 * @param {string} props.offerDetails.offers[0].offer.termsAndConditionsUrl - The URL of the offer's terms and conditions.
 * @param {Object} [props.offerDetails.offers[0].offer.styling] - (Optional) Styling information for the offer's title.
 * @param {Array} [props.offerDetails.offers[0].offer.styling.boldTitleTerms] - An array of strings representing terms in the title that should be bolded.
 * @returns {JSX.Element|null} The rendered offer banner component or null if offerDetails is not provided.
 */

const ResultPageOfferBanner = () => {
    const offerDetails = useSelector((state) => getOfferDetails(state));
    const dispatchOfferShow = () => useDispatch(offerShow);
    const quoteCount = useSelector((state) => availableProductResultsSelector(state));
    let boldTitleTerms = [];
    if (offerDetails && offerDetails.offers) {
        const offerShown = Boolean(offerDetails.offers.length > 0) && quoteCount > 0;

        if (!offerShown) {
            return <></>;
        }

        if (offerShown) {
            dispatchOfferShow();
        }

        const isXsBreakpoint = pageContext.getProps().pageState.breakpoint === 'xs';

        const offerTitle = offerDetails.offers[0].offer.title;
        const offerImageUrl = offerDetails.offers[0].offer.imageUrl;
        const offerTCUrl = offerDetails.offers[0].offer.termsAndConditionsUrl;

        if (offerDetails.offers[0].offer.hasOwnProperty('styling')) {
            boldTitleTerms = offerDetails.offers[0].offer.styling.boldTitleTerms;
        } else {
            boldTitleTerms = [];
        }

        const formatTitle = () => {
            let formattedTitle = offerTitle;
            if (boldTitleTerms !== []) {
                boldTitleTerms.forEach((term) => {
                    const escapedTerm = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                    const regex = new RegExp(escapedTerm, 'gi');
                    formattedTitle = formattedTitle.replace(regex, `<strong>${term}</strong>`);
                });
                return formattedTitle;
            }
            return formattedTitle;
        };

        if (isXsBreakpoint) {
            return (
                <div className={styles.mobileBannerContainer}>
                    <div className={styles.mobileLeftBannerContainer}>
                        <img src={offerImageUrl} height="50px" width="120px" alt="offer-banner" />
                        <div className={styles.mobileMessage}>
                            <p dangerouslySetInnerHTML={{ __html: sanitizeInnerHtml(formatTitle()) }} />
                        </div>
                    </div>
                    <div className={styles.rightBannerContainer}>
                        <p className={styles.termsAndConditions}>
                            <a href={offerTCUrl} target="_blank" rel="noreferrer">
                                <u> T&Cs apply </u>
                            </a>
                        </p>
                    </div>
                </div>
            );
        }

        return (
            <div className={styles.bannerContainer}>
                <div className={styles.leftBannerContainer}>
                    <div className={styles.message}>
                        <p dangerouslySetInnerHTML={{ __html: sanitizeInnerHtml(formatTitle()) }} />
                    </div>
                </div>
                <div className={styles.rightBannerContainer}>
                    <img src={offerImageUrl} alt="" height="80px" width="150px" />
                    <p className={styles.termsAndConditions}>
                        <a href={offerTCUrl} target="_blank" rel="noreferrer">
                            <u> T&Cs apply </u>
                        </a>
                    </p>
                </div>
            </div>
        );
    }
    return <></>;
};

export { ResultPageOfferBanner };
